import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRow,
  IonSearchbar,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { closeOutline, informationCircle } from "ionicons/icons";
import React, { useMemo, useRef, useState } from "react";
import firebaseEventConfig from "../../../utils/firebaseAnalytics/firebaseEventConfig";
import { CMEDfirebaseLogEvent } from "../../../utils/firebaseAnalytics/firebaseUtils";
import { OPTION_CATEGORY_CONFIG } from "./optionsGridConfig/optionCategoryConfig";

/**
 * @author
 * E45597 Aryamitra Chaudhuri
 */
type TermData = {
  OptionCategory: string;
  TermStructureCode: string;
  TermStructureDescription: string;
};

type OriginalData = {
  [key: string]: TermData[];
};

type OptionsGridSearchModalProps = {
  isOptionsGridSearchModalOpen: boolean;
  setIsOptionsGridSearchModalOpen: Function;
  setIsOptionItemClicked: Function;
  optionsListData: any;
  selectOption: any;
};

const OptionsGridSearchModal: React.FC<OptionsGridSearchModalProps> = ({
  isOptionsGridSearchModalOpen,
  setIsOptionsGridSearchModalOpen,
  setIsOptionItemClicked,
  optionsListData,
  selectOption,
}) => {
  const [searchVal, setSearchVal] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<TermData[]>([]);
  const modal = useRef<HTMLIonModalElement>(null);

  // Fallback check to accommodate either structure: optionsListData.payload or optionsListData
  const dataToProcess: OriginalData =
    optionsListData?.payload || optionsListData;

  const sortedData = useMemo(() => {
    if (!dataToProcess) return [];
    const validKeys = Object.keys(dataToProcess);
    validKeys.sort((a, b) => {
      const orderA = OPTION_CATEGORY_CONFIG[a]
        ? OPTION_CATEGORY_CONFIG[a].order
        : -1;
      const orderB = OPTION_CATEGORY_CONFIG[b]
        ? OPTION_CATEGORY_CONFIG[b].order
        : -1;
      return orderA - orderB;
    });
    return validKeys.map((key) => dataToProcess[key]);
  }, [dataToProcess]);

  const filteredData = useMemo(() => {
    if (!searchVal) return sortedData;
    return sortedData
      .map((group) =>
        group.filter((item: TermData) =>
          item?.TermStructureDescription?.toLowerCase().includes(
            searchVal.toLowerCase(),
          ),
        ),
      )
      .filter((group) => group.length > 0);
  }, [sortedData, searchVal]);

  const handleOptionCheckboxChange = (event: any, item: TermData) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setSelectedOptions((prevOptions) => [...prevOptions, item]);
    } else {
      setSelectedOptions((prevOptions) =>
        prevOptions.filter(
          (option) => option.TermStructureCode !== item.TermStructureCode,
        ),
      );
    }
  };

  const handleSelectButtonClick = () => {
    // Process selected options
    selectOption(selectedOptions);

    // Reset state and close modal
    setSelectedOptions([]);
    setIsOptionItemClicked(true);
    setIsOptionsGridSearchModalOpen(false);

    for (let index = 0; index < selectedOptions?.length; index++) {
      CMEDfirebaseLogEvent(
        firebaseEventConfig.CMEDComponentNames.CMED_OPTIONS_GRID,
        firebaseEventConfig.eventNames.CMED_OPTIONS_GRID
          .CMED_OPTIONS_GRID_ADD_NEW_INSTRUMENT,
      );
    }
  };

  return (
    <IonModal isOpen={isOptionsGridSearchModalOpen} ref={modal}>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>
            <p className="ion-no-margin text-bold">OPTIONS LIST</p>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => {
                setIsOptionsGridSearchModalOpen(false);
                setSelectedOptions([]);
                setSearchVal("");
              }}
            >
              <IonIcon
                icon={closeOutline}
                color="dark"
                slot="icon-only"
              ></IonIcon>
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            onClick={() => modal.current?.setCurrentBreakpoint(1.0)}
            placeholder="Search..."
            onIonInput={(event: any) => setSearchVal(event.detail.value)}
          ></IonSearchbar>
        </IonToolbar>
      </IonHeader>
      {filteredData.length === 0 && (
        <IonList inset={true}>
          <IonItem lines="none" color="warning">
            <IonIcon icon={informationCircle} slot="start"></IonIcon>
            <IonLabel>No search result.</IonLabel>
          </IonItem>
        </IonList>
      )}
      <IonContent>
        {filteredData.flat().map((item: TermData, index: number) => (
          <IonItem
            key={item?.TermStructureCode}
            className={index % 2 === 0 ? "even_item_bg" : ""}
            lines="full"
          >
            <IonCheckbox
              labelPlacement="end"
              mode="md"
              justify="start"
              checked={selectedOptions.some(
                (option) => option.TermStructureCode === item.TermStructureCode,
              )}
              value={item?.TermStructureDescription}
              onIonChange={(e) => handleOptionCheckboxChange(e, item)}
            >
              {item?.TermStructureDescription}
            </IonCheckbox>
          </IonItem>
        ))}
      </IonContent>
      <IonFooter
        className="ion-no-padding ion-no-border"
        style={{ marginBottom: "20px" }}
      >
        <IonGrid className="ion-padding">
          <IonRow>
            <IonCol size="12">
              <IonButton
                expand="block"
                onClick={handleSelectButtonClick}
                disabled={selectedOptions.length === 0}
                className="custom_cmed_mobile_button"
              >
                Select
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonFooter>
    </IonModal>
  );
};

export default OptionsGridSearchModal;
